import React from 'react';
import { Amount, decimalsToWei } from '@akropolis-web/primitives';

import { MRT_ColumnDef, ToggleLowSupplyFilterButton, amountToAccessorValue } from 'components/MRT';
import {
  FallbackedTotalUSDWithProgress,
  FormattedAmount,
  Progress,
  RatingLiteral,
  TokenName,
} from 'components';
import { FormattedAmountProps } from 'components/FormattedAmount/types';
import { SystemStyleObject } from 'core/styles';
import { ratingToLetter } from 'domain/types';
import { getAmount } from 'domain/utils';
import { left } from 'utils/either';

import { AssetsTableData } from '../../types';

const headCellAlignStyles = (): SystemStyleObject => ({
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'flex-end',
  },
});

const numberFilters = ['between', 'lessThan', 'greaterThan'];

const shortFormattedAmountProps: Omit<FormattedAmountProps<Amount>, 'sum'> = {
  precision: 0,
};

const shortFormattedAmount24HProps: Omit<FormattedAmountProps<Amount>, 'sum'> = {
  precision: 0,
  hasSign: true,
};

export const columns: MRT_ColumnDef<AssetsTableData>[] = [
  {
    header: 'Rating',
    accessorFn: originalRow => originalRow.rating.fold(ratingToLetter, () => undefined),
    id: 'rating',
    columnFilterModeOptions: ['fuzzy', 'contains', 'startsWith', 'endsWith'],
    filterFn: 'fuzzy',
    enableColumnActions: false,
    size: 100,
    sortingFn: 'basic',
    invertSorting: true,
    Cell: ({ row }) => <RatingLiteral rating={row.original.rating} colored />,
    muiTableHeadCellProps: {
      sx: {
        paddingLeft: '12px',
      },
    },
    muiTableBodyCellProps: {
      align: 'left',
      sx: {
        fontSize: '20px',
        paddingLeft: '12px',
      },
    },
  },
  {
    header: 'Asset',
    accessorFn: originalRow => originalRow.token.symbol,
    enableColumnActions: false,
    id: 'asset',
    size: 170,
    Cell: ({ row, renderedCellValue }) => (
      <TokenName symbol={renderedCellValue} token={row.original.token} />
    ),
  },
  {
    header: 'Price',
    accessorFn: originalRow =>
      originalRow.price.fold(
        p => p,
        () => undefined,
      ),
    id: 'price',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 110,
    maxSize: 110,
    size: 110,
    Cell: ({ row }) =>
      row.original.price.fold(
        price => {
          return (
            <FormattedAmount sum={getAmount(price, '$').mul(decimalsToWei(18))} precision={3} />
          );
        },
        error => <FormattedAmount symbolSize="inherit" sum={left(error)} />,
      ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Collateral, current',
    accessorFn: originalRow => {
      return amountToAccessorValue(originalRow.collateral.amount.value);
    },
    id: 'totalCollateral',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 200,
    maxSize: 200,
    size: 200,
    Cell: ({ row }) => (
      <FallbackedTotalUSDWithProgress
        {...row.original.collateral.amount}
        formattedAmountProps={shortFormattedAmountProps}
        progress={row.original.collateral.progress}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Collateral, 24H',
    accessorFn: originalRow => amountToAccessorValue(originalRow.collateralDiff24H.amount.value),
    id: 'collateral24',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'diffs',
    minSize: 160,
    maxSize: 160,
    size: 160,
    Cell: ({ row }) => (
      <FallbackedTotalUSDWithProgress
        {...row.original.collateralDiff24H.amount}
        formattedAmountProps={shortFormattedAmount24HProps}
        progress={row.original.collateralDiff24H.progress}
        colored
        getProgressColor={Progress.getSignColor}
        renderProgressLabel={() => null}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Borrows, current',
    accessorFn: originalRow => amountToAccessorValue(originalRow.borrow.amount.value),
    id: 'totalBorrow',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 190,
    maxSize: 190,
    size: 190,
    Cell: ({ row }) => (
      <FallbackedTotalUSDWithProgress
        {...row.original.borrow.amount}
        formattedAmountProps={shortFormattedAmountProps}
        progress={row.original.borrow.progress}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Borrows, 24H',
    accessorFn: originalRow => amountToAccessorValue(originalRow.borrowDiff24H.amount.value),
    id: 'borrows24',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'diffs',
    minSize: 140,
    maxSize: 140,
    size: 140,
    Cell: ({ row }) => (
      <FallbackedTotalUSDWithProgress
        {...row.original.borrowDiff24H.amount}
        formattedAmountProps={shortFormattedAmount24HProps}
        progress={row.original.borrowDiff24H.progress}
        colored
        getProgressColor={Progress.getSignColor}
        renderProgressLabel={() => null}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  ToggleLowSupplyFilterButton.getLowSupplyColumn(),
];
